import {ChangeDetectionStrategy, Component, effect, ElementRef, Inject, Injectable, InjectionToken, input, output, Signal, viewChild} from "@angular/core";
import {GruppeId, GruppeMedlemListeModell, OpprettKommentarSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {CdkTextareaAutosize} from "@angular/cdk/text-field";
import {MatError, MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {TranslateModule} from "@ngx-translate/core";
import {MatButton} from "@angular/material/button";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {VelgMedlemmerKomponent} from "web-ng2/delt/komponenter/skjema/velg-medlemmer/velg-medlemmer";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {MatTooltip} from "@angular/material/tooltip";
import {concatMap, Observable, take} from "rxjs";
import {map} from "rxjs/operators";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {toSignal} from "@angular/core/rxjs-interop";
import {erIkkeTomStreng, erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {nyUuid} from "felles-ng2/støttefunksjoner/uuidGenerator";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {GruppemedlemDatatjeneste} from "felles-ng2/tjenester/datalager/GruppemedlemDatatjeneste";
import {InlineSkjemaKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema";
import {InlineSkjemaHandlingerKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema-handlinger/inline-skjema-handlinger";
import {NgClass} from "@angular/common";
import {NavigasjonDeaktivering} from "web-ng2/tjenester/NavigasjonDeaktivering";
import {NavigasjonforhindretFlashDirektiv} from "web-ng2/delt/direktiver/navigasjonforhindret-flash/navigasjonforhindret-flash";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {medlemmerAktive} from "felles-ng2/modell/medlemModellHjelpere";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {TilpassAutosizeVedBreddeendringDirektiv} from "web-ng2/delt/direktiver/tilpassAutosizeVedBreddeendring/tilpassAutosizeVedBreddeendring";

const debug = initDebugger('NyKommentarTraadKomponent');

/**  InjectionToken for injisering av {@link NyKommentarTraadKomponentAvhengigheter} implementasjon. */
export const NyKommentarTraadKomponentAvhengigheter_DI_TOKEN = new InjectionToken<NyKommentarTraadKomponentAvhengigheter>('NyKommentarTraadKomponentAvhengigheter implementasjon');

@Component({
    selector: 'ny-kommentar-traad',
    templateUrl: 'ny-kommentar-traad.html',
    styleUrl: 'ny-kommentar-traad.scss',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormField,
        MatError,
        MatInput,
        MatLabel,
        MatButton,
        MatSlideToggle,
        MatTooltip,
        CdkTextareaAutosize,
        TilpassAutosizeVedBreddeendringDirektiv,
        StandardValideringsfeilPipe,
        TranslateModule,
        VisSpinnerDirektiv,
        VelgMedlemmerKomponent,
        InlineSkjemaKomponent,
        InlineSkjemaHandlingerKomponent,
        NgClass,
        NavigasjonforhindretFlashDirektiv,
        InlineMeldingKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NyKommentarTraadKomponent {

    private nyKommentarFelt = viewChild.required('nyKommentarFelt', {read: ElementRef<HTMLInputElement>});

    vedFulltSkjemaAktivert = output<boolean>();

    gruppeId = input.required<GruppeId>();

    skjema = new FormGroup({
        kommentar: new FormControl('', {nonNullable: true, validators: FeltValidatorer.storStreng}),
        privat: new FormControl(true, {nonNullable: true}),
        varsledeMedlemmer: new FormControl<GruppeMedlemListeModell[]>([], {nonNullable: true}),
    });

    medlemmer!: Signal<GruppeMedlemListeModell[]>;
    visFulltSkjema!: Signal<boolean>;
    lagrer = asynkArbeidPågårSignal();

    constructor(
      @Inject(NyKommentarTraadKomponentAvhengigheter_DI_TOKEN) private avhengigheter: NyKommentarTraadKomponentAvhengigheter,
      private lastMedlemmer: LastMedlemmerAvhengighet,
      private brukerkontekstTilbyder: BrukerkontekstTilbyder,
      private navigasjonDeaktivering: NavigasjonDeaktivering) {
        //last medlemmer nå bruker skriver første tegn i kommentarfelt
        this.medlemmer = toSignal(this.skjema.controls.kommentar.valueChanges
          .pipe(
            take(1),
            concatMap(() => this.medlemmerUnntattInnloggetBruker())
          ), {initialValue: []});

        //aktiver fullt skjem når kommentarfelt inneholder tekst
        this.visFulltSkjema = toSignal(this.skjema.controls.kommentar.valueChanges
          .pipe(map(kommentar => erIkkeTomStreng(kommentar))), {initialValue: false});

        //informer kaller om at fullt skjema er aktivert/deaktivert
        effect(() => {
            this.vedFulltSkjemaAktivert.emit(this.visFulltSkjema())
            this.navigasjonDeaktivering.aktiverNavigasjon(!this.visFulltSkjema());
        });
    }

    /** Flytter markør inn i kommentar-tekstfeltet */
    fokus() {
        this.nyKommentarFelt()?.nativeElement.focus();
    }

    async registrer() {
        //vi har ikke definert kommentar som required (for å unngå at skjema vises rødmerket når det ikke er i bruk)
        if (erTomEllerNil(this.skjema.value.kommentar)) return;

        //når kaller er migrert til ng2 kan opprettelse samt info om sak/oppgave delegeres til kaller vi avhengighet injisert av kaller
        await this.lagrer.utfør(async () => {
            await this.avhengigheter.opprettKommentar(this.somSpesifikasjon());
            this.nullstillSkjema();
        });
    }

    avbryt() {
        this.nullstillSkjema();
    }

    //private hjelpemetoder

    private medlemmerUnntattInnloggetBruker(): Observable<GruppeMedlemListeModell[]> {
        const kallendeBrukerId = this.brukerkontekstTilbyder.autentisertBruker().bruker.id;
        return this.lastMedlemmer.medlemmer(this.gruppeId())
          .pipe(
            map(medlemmer => medlemmer.filter(medlem => medlem.brukerId !== kallendeBrukerId)),
            map(medlemmerAktive))
    }

    private somSpesifikasjon(): OpprettKommentarSpesifikasjon {
        const skjemaverdier = this.skjema.value;
        return {
            id: nyUuid(),
            kommentar: skjemaverdier.kommentar!,
            privat: skjemaverdier.privat!,
            varslede: skjemaverdier.varsledeMedlemmer!.map(medlem => medlem.id)
        }
    }

    private nullstillSkjema() {
        this.skjema.controls.kommentar.setValue('');
        this.skjema.controls.privat.setValue(true);
        this.skjema.controls.varsledeMedlemmer.setValue([]);
    }
}

@Injectable()
export class LastMedlemmerAvhengighet {

    constructor(private gruppemedlemDatatjeneste: GruppemedlemDatatjeneste) {
    }

    medlemmer(gruppeId: GruppeId): Observable<GruppeMedlemListeModell[]> {
        return this.gruppemedlemDatatjeneste.finnTilknyttet(gruppeId);
    }
}

export interface NyKommentarTraadKomponentAvhengigheter {
    opprettKommentar(spesifikasjon: OpprettKommentarSpesifikasjon): Promise<void>;
}
